import React, { useState, useEffect } from 'react';
import {
    Upload,
    Button,
    Table,
    Alert,
    Card,
    Row,
    Col,
    Statistic,
    Space,
    Tag,
    Select,
    Switch,
    Form,
    Divider,
    Typography,
    Tabs,
    message,
    Spin,
} from 'antd';
import {
    UploadOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    TableOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    InboxOutlined,
    PlayCircleOutlined,
    WarningOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { Title, Text } = Typography;
const { Dragger } = Upload;
const { TabPane } = Tabs;

const ExcelInputNodeProperties = ({ data = {}, updateNodeData }) => {
    // Lấy config từ data.config (nơi dữ liệu được lưu)
    const config = data?.config || {};
    
    const [loading, setLoading] = useState(false);
    const [fileName, setFileName] = useState(config?.fileName || '');
    const [fileType, setFileType] = useState(config?.fileType || '');
    const [sheets, setSheets] = useState(() => {
        const initialSheets = config?.sheets || [];
        // Create deep copies to avoid mutation of read-only objects
        return initialSheets.map(sheet => ({
            ...sheet,
            headers: sheet.schema ? sheet.schema.map(col => col.columnName) : [],
            schema: sheet.schema ? sheet.schema.map(col => ({
                ...col,
                sampleValues: col.sampleValues || ['Sample 1', 'Sample 2', 'Sample 3']
            })) : []
        }));
    });
    const [selectedSheet, setSelectedSheet] = useState(() => {
        const initialSheets = config?.sheets || [];
        if (initialSheets.length > 0) {
            const foundSheet = initialSheets.find(s => s.name === config?.selectedSheet) || initialSheets[0];
            // Create a deep copy to avoid mutation of read-only objects
            if (foundSheet) {
                return {
                    ...foundSheet,
                    headers: foundSheet.schema ? foundSheet.schema.map(col => col.columnName) : [],
                    schema: foundSheet.schema ? foundSheet.schema.map(col => ({
                        ...col,
                        sampleValues: col.sampleValues || ['Sample 1', 'Sample 2', 'Sample 3']
                    })) : []
                };
            }
        }
        return null;
    });
    const [selectedSheetIndex, setSelectedSheetIndex] = useState(config?.selectedSheetIndex || 0);

    // Debug logging for state changes
    useEffect(() => {
        console.log('ExcelInputNodeProperties state update:');
        console.log('- fileName:', fileName);
        console.log('- fileType:', fileType);
        console.log('- sheets.length:', sheets.length);
        console.log('- loading:', loading);
        console.log('- selectedSheet:', selectedSheet);
    }, [fileName, fileType, sheets, loading, selectedSheet]);

    // Ensure updateNodeData is a function and preserve nodeType
    const safeUpdateNodeData = (newData) => {
        if (typeof updateNodeData === 'function') {
            // Always preserve existing nodeType and other important fields
            const preservedData = {
                nodeType: data?.nodeType || data?.type || 'excel_input',
                type: data?.type || 'excel_input',
                label: data?.label,
                onNodeClick: data?.onNodeClick,
                ...newData // New data will override any common fields
            };
            updateNodeData(preservedData);
        }
    };

    // Detect data type from value
    const detectDataType = (value) => {
        if (value === null || value === undefined || value === '') {
            return 'string';
        }

        const strValue = String(value).trim();

        // Check for number
        const numValue = Number(strValue);
        if (!isNaN(numValue) && isFinite(numValue)) {
            return strValue.includes('.') ? 'float' : 'integer';
        }

        // Check for boolean
        if (['true', 'false', 'yes', 'no', '1', '0'].includes(strValue.toLowerCase())) {
            return 'boolean';
        }

        // Check for date patterns
        const datePatterns = [
            /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
            /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
            /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
        ];

        if (datePatterns.some(pattern => pattern.test(strValue))) {
            return 'date';
        }

        return 'string';
    };

    // Enhanced CSV parsing with better delimiter detection
    const parseCSV = (text, delimiter = null) => {
        const lines = text.split('\n').filter(line => line.trim() !== '');
        if (lines.length < 1) return { headers: [], rows: [], delimiter: ',' };

        // Auto-detect delimiter if not provided
        if (!delimiter) {
            const delimiters = [',', ';', '\t', '|'];
            const firstLine = lines[0];

            let bestDelimiter = ',';
            let maxColumns = 0;

            delimiters.forEach(del => {
                const columns = firstLine.split(del).length;
                if (columns > maxColumns) {
                    maxColumns = columns;
                    bestDelimiter = del;
                }
            });

            delimiter = bestDelimiter;
        }

        // Parse with detected/specified delimiter
        const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                const nextChar = line[i + 1];

                if (char === '"') {
                    if (inQuotes && nextChar === '"') {
                        current += '"';
                        i++; // Skip next quote
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (char === delimiter && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }

            result.push(current.trim());
            return result.map(val => val.replace(/^"|"$/g, ''));
        };

        // Parse headers
        const headers = parseCSVLine(lines[0]);

        // Parse data rows (take more for better analysis)
        const rows = [];
        for (let i = 1; i < Math.min(10, lines.length); i++) {
            const values = parseCSVLine(lines[i]);
            // Ensure all rows have same number of columns as headers
            while (values.length < headers.length) {
                values.push('');
            }
            rows.push(values.slice(0, headers.length));
        }

        return { headers, rows, delimiter, totalRows: lines.length - 1 };
    };

    // Real Excel parsing using XLSX library
    const parseExcel = async (arrayBuffer) => {
        try {
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });

            const sheets = workbook.SheetNames.map(sheetName => {
                const worksheet = workbook.Sheets[sheetName];
                const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

                // Get all data as array of arrays
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                    range: range
                });

                if (jsonData.length === 0) {
                    return {
                        name: sheetName,
                        headers: [],
                        rows: [],
                        totalRows: 0
                    };
                }

                // First row as headers
                const headers = jsonData[0]?.map(h => String(h || '').trim()) || [];

                // Filter out empty headers and corresponding columns
                const validColumnIndexes = [];
                const validHeaders = [];
                headers.forEach((header, index) => {
                    if (header && header !== '') {
                        validColumnIndexes.push(index);
                        validHeaders.push(header);
                    }
                });

                // Get rows with only valid columns (take sample for preview)
                const allRows = jsonData.slice(1);
                const rows = allRows.slice(0, 10).map(row => {
                    return validColumnIndexes.map(index => {
                        const value = row[index];
                        return value !== undefined && value !== null ? String(value) : '';
                    });
                });

                return {
                    name: sheetName,
                    headers: validHeaders,
                    rows: rows,
                    totalRows: allRows.length,
                    range: worksheet['!ref']
                };
            });

            return sheets.filter(sheet => sheet.headers.length > 0);
        } catch (error) {
            console.error('Excel parsing error:', error);
            throw new Error('Failed to parse Excel file: ' + error.message);
        }
    };

    // Analyze schema from parsed data - Optimized for lightweight storage
    const analyzeSchema = (headers, rows) => {
        const schemaAnalysis = headers.map((header, index) => {
            const columnValues = rows.map(row => row[index]).filter(val => val !== undefined && val !== '');

            // Detect most common type
            const types = columnValues.map(detectDataType);
            const typeCount = types.reduce((acc, type) => {
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {});

            const mostCommonType = Object.keys(typeCount).reduce((a, b) =>
                typeCount[a] > typeCount[b] ? a : b, 'string'
            );

            // Only store essential schema information for storage
            return {
                key: index,
                columnName: header,
                dataType: mostCommonType,
                nullable: columnValues.length === 0 || columnValues.some(val => val === '' || val === null),
                unique: new Set(columnValues).size === columnValues.length && columnValues.length > 0,
                // Keep sample values for UI display only
                sampleValues: columnValues.slice(0, 3).length > 0 ? columnValues.slice(0, 3) : ['(empty)']
            };
        });

        return schemaAnalysis;
    };

    // Create ultra-lightweight schema for storage (minimal data only)
    const createMinimalSchema = (schema) => {
        return schema.map(col => ({
            key: col.key,
            columnName: col.columnName,
            dataType: col.dataType,
            nullable: col.nullable,
            unique: col.unique
            // Exclude sampleValues, previewData - only metadata needed for execution
        }));
    };

    // Enhance minimal schema with sample values for UI display
    const enhanceSchemaForUI = (minimalSchema, sampleValues = null) => {
        return minimalSchema.map((col, index) => ({
            ...col,
            // Add sample values for UI display only, not stored
            sampleValues: sampleValues?.[index] || ['Sample 1', 'Sample 2', 'Sample 3']
        }));
    };

    // Handle file upload with real parsing
    const handleFileUpload = {
        name: 'excelFile',
        multiple: false,
        accept: '.csv,.xlsx,.xls',
        beforeUpload: (file) => {
            const validTypes = ['.csv', '.xlsx', '.xls'];
            const fileExt = '.' + file.name.split('.').pop().toLowerCase();
            if (!validTypes.includes(fileExt)) {
                message.error('Please upload CSV or Excel files only');
                return false;
            }
            return false; // Prevent auto upload
        },
        onChange: async (info) => {
            const { file, fileList } = info;
            console.log('File upload onChange triggered:', file);
            console.log('File status:', file.status);
            console.log('File originFileObj exists:', !!file.originFileObj);
            console.log('FileList length:', fileList?.length);
            console.log('File object itself:', file);

            // Fix: Khi beforeUpload return false, file sẽ không có status và originFileObj
            // Trong trường hợp này, file object chính là file cần xử lý
            if (file && (file.originFileObj || file.status === 'done' || file.status === 'uploading' || file.status === undefined)) {
                console.log('Processing file:', file.name);

                // Reset state trước khi xử lý
                setSheets([]);
                setSelectedSheet(null);
                setSelectedSheetIndex(0);
                setLoading(true);
                setFileName(file.name);

                const fileExt = '.' + file.name.split('.').pop().toLowerCase();
                const isExcel = ['.xlsx', '.xls'].includes(fileExt);
                setFileType(isExcel ? 'excel' : 'csv');
                console.log('File type detected:', isExcel ? 'excel' : 'csv');

                try {
                    // Lấy file object để đọc - khi beforeUpload return false, file chính là file cần xử lý
                    const fileToRead = file.originFileObj || file;

                    if (!fileToRead) {
                        console.error('No file object found to read');
                        message.error('No valid file found to process');
                        setLoading(false);
                        return;
                    }

                    console.log('Reading file:', fileToRead.name || file.name);
                    console.log('File to read type:', typeof fileToRead);
                    console.log('File to read constructor:', fileToRead.constructor.name);

                    const reader = new FileReader();

                    reader.onload = async (e) => {
                        try {
                            console.log('File read successfully, parsing...');
                            let parsedSheets = [];

                            if (isExcel) {
                                // Parse Excel file
                                console.log('Parsing Excel file...');
                                parsedSheets = await parseExcel(e.target.result);
                                console.log('Excel parsed, sheets found:', parsedSheets.length);
                            } else {
                                // Parse CSV file
                                console.log('Parsing CSV file...');
                                const text = e.target.result;
                                const csvResult = parseCSV(text);

                                if (csvResult.headers.length === 0) {
                                    throw new Error('No valid CSV headers found');
                                }

                                parsedSheets = [{
                                    name: 'CSV Data',
                                    headers: csvResult.headers,
                                    rows: csvResult.rows,
                                    totalRows: csvResult.totalRows,
                                    delimiter: csvResult.delimiter
                                }];
                                console.log('CSV parsed successfully');
                            }

                            // Analyze schema for each sheet
                            console.log('Analyzing schema for sheets...');
                            const sheetsWithSchema = parsedSheets.map((sheet) => {
                                const schema = analyzeSchema(sheet.headers, sheet.rows);
                                const minimalSchema = createMinimalSchema(schema);

                                return {
                                    name: sheet.name,
                                    headers: sheet.headers,
                                    totalRows: sheet.totalRows,
                                    range: sheet.range,
                                    // Keep full schema with sampleValues for UI display only
                                    schema: schema, // For UI rendering with sample values
                                    // Keep minimal schema for storage optimization
                                    minimalSchema: minimalSchema
                                };
                            });

                            console.log('Schema analysis complete, updating state...');
                            console.log('Sheets with schema:', sheetsWithSchema);

                            // Force update state và trigger re-render
                            setSheets(sheetsWithSchema);
                            setSelectedSheet(sheetsWithSchema[0]);
                            setSelectedSheetIndex(0);

                            // Create ultra-lightweight data structure for node storage - OPTIMIZED
                            // CHỈ LƯU TRONG CONFIG - không duplicate ở root level
                            const optimizedSheets = sheetsWithSchema.map(sheet => ({
                                name: sheet.name,
                                totalRows: sheet.totalRows,
                                // Chỉ giữ minimal schema - không cần headers riêng (đã có trong schema)
                                schema: sheet.minimalSchema
                            }));

                            // Generate meaningful node name from file only
                            const nodeName = file.name.replace(/\.(xlsx|xls|csv)$/i, ''); // Remove extension
                            
                            console.log('Auto-updating Excel input node name to:', nodeName);

                            // Update node data - CHỈ LƯU TRONG CONFIG, KHÔNG DUPLICATE
                            safeUpdateNodeData({
                                customName: nodeName,
                                label: nodeName,
                                // Tất cả thông tin chỉ nằm trong config
                                config: {
                                    fileName: file.name,
                                    fileType: isExcel ? 'excel' : 'csv',
                                    sheets: optimizedSheets,
                                    selectedSheet: sheetsWithSchema[0]?.name,
                                    selectedSheetIndex: 0
                                    // Không lưu: headers, schema riêng - đã có trong sheets[].schema
                                }
                                // KHÔNG DUPLICATE ở root level nữa
                            });

                            console.log('State updated successfully');
                            
                            // Log storage optimization info
                            const rawDataSize = JSON.stringify(sheetsWithSchema).length;
                            const optimizedDataSize = JSON.stringify(optimizedSheets).length;
                            console.log(`Storage optimization: ${rawDataSize} → ${optimizedDataSize} bytes (${((optimizedDataSize / rawDataSize) * 100).toFixed(1)}% of original)`);
                            
                            message.success(`File loaded successfully! Found ${sheetsWithSchema.length} sheet(s) with ${sheetsWithSchema[0]?.totalRows || 0} rows. Storage optimized.`);
                        } catch (error) {
                            console.error('Parsing error:', error);
                            message.error('Error parsing file: ' + error.message);
                            // Reset state on error
                            setSheets([]);
                            setSelectedSheet(null);
                        } finally {
                            setLoading(false);
                        }
                    };

                    reader.onerror = () => {
                        console.error('FileReader error');
                        message.error('Error reading file');
                        setLoading(false);
                        setSheets([]);
                        setSelectedSheet(null);
                    };

                    // Read file based on type
                    if (isExcel) {
                        reader.readAsArrayBuffer(fileToRead);
                    } else {
                        reader.readAsText(fileToRead);
                    }
                } catch (error) {
                    console.error('Upload error:', error);
                    message.error('Error processing file: ' + error.message);
                    setLoading(false);
                    setSheets([]);
                    setSelectedSheet(null);
                }
            } else if (file.status === 'removed') {
                console.log('File removed, resetting state...');
                setFileName('');
                setFileType('');
                setSheets([]);
                setSelectedSheet(null);
                setSelectedSheetIndex(0);
                setLoading(false);

                // Reset node data - CHỈ TRONG CONFIG
                safeUpdateNodeData({
                    customName: '',
                    label: 'Excel Input',
                    config: {}
                });
                
                console.log('File removed and data reset - storage optimized');
            } else {
                console.log('File not ready for processing, status:', file.status);
            }
        }
    };

    // Handle sheet selection
    const handleSheetChange = (sheetName) => {
        const sheet = sheets.find(s => s.name === sheetName);
        if (!sheet) return;
        
        const sheetIndex = sheets.findIndex(s => s.name === sheetName);
        
        // Generate meaningful node name from file only (no sheet name)
        const nodeName = fileName.replace(/\.(xlsx|xls|csv)$/i, ''); // Remove extension
        
        console.log('Auto-updating Excel input node name to:', nodeName);
        
        setSelectedSheet(sheet);
        setSelectedSheetIndex(sheetIndex);
        
        // CHỈ CẬP NHẬT TRONG CONFIG - không duplicate
        safeUpdateNodeData({
            customName: nodeName,
            label: nodeName,
            config: {
                ...data?.config,
                selectedSheet: sheetName,
                selectedSheetIndex: sheetIndex
            }
        });
    };

    // Handle sheet selection by index (for compatibility)
    const handleSheetIndexChange = (index) => {
        const sheet = sheets[index];
        if (!sheet) return;
        
        // Generate meaningful node name from file only (no sheet name)
        const nodeName = fileName.replace(/\.(xlsx|xls|csv)$/i, ''); // Remove extension
        
        console.log('Auto-updating Excel input node name to:', nodeName);
        
        setSelectedSheet(sheet);
        setSelectedSheetIndex(index);
        
        // CHỈ CẬP NHẬT TRONG CONFIG - không duplicate
        safeUpdateNodeData({
            customName: nodeName,
            label: nodeName,
            config: {
                ...data?.config,
                selectedSheet: sheet.name,
                selectedSheetIndex: index
            }
        });
    };

    // Generate preview data for table display (from schema, not stored data)
    const generatePreviewData = (sheet) => {
        if (!sheet || !sheet.schema) return [];
        
        // Generate sample preview based on schema instead of stored data
        return Array(Math.min(10, 3)).fill(null).map((_, rowIndex) => {
            const rowData = { key: rowIndex };
            sheet.schema.forEach(col => {
                // Generate sample data based on column type
                switch (col.dataType) {
                    case 'integer':
                        rowData[col.columnName] = `${Math.floor(Math.random() * 100)}`;
                        break;
                    case 'float':
                        rowData[col.columnName] = `${(Math.random() * 100).toFixed(2)}`;
                        break;
                    case 'boolean':
                        rowData[col.columnName] = Math.random() > 0.5 ? 'true' : 'false';
                        break;
                    case 'date':
                        rowData[col.columnName] = new Date().toISOString().split('T')[0];
                        break;
                    default:
                        rowData[col.columnName] = col.sampleValues?.[Math.min(rowIndex, col.sampleValues.length - 1)] || `Sample ${col.columnName}`;
                }
            });
            return rowData;
        });
    };

    // Get data type tag color
    const getTypeColor = (type) => {
        const colors = {
            'string': 'blue',
            'integer': 'green',
            'float': 'orange',
            'boolean': 'purple',
            'date': 'red'
        };
        return colors[type] || 'default';
    };

    // Update selected sheet schema (for editing schema)
    const updateSheetSchema = (sheetIndex, columnIndex, updates) => {
        if (!sheets[sheetIndex] || !sheets[sheetIndex].schema || !sheets[sheetIndex].schema[columnIndex]) {
            console.warn('Invalid sheet or schema index:', { sheetIndex, columnIndex, sheets });
            return;
        }
        
        const newSheets = [...sheets];
        newSheets[sheetIndex].schema[columnIndex] = {
            ...newSheets[sheetIndex].schema[columnIndex],
            ...updates
        };
        setSheets(newSheets);
        
        // Also update minimal schema for storage
        if (newSheets[sheetIndex].minimalSchema) {
            newSheets[sheetIndex].minimalSchema[columnIndex] = createMinimalSchema([{
                ...newSheets[sheetIndex].schema[columnIndex],
                ...updates
            }])[0];
        }
    };

    // Enhanced schema loading from existing data (with backward compatibility)
    // Đọc từ config thay vì data trực tiếp
    useEffect(() => {
        const configSheets = config?.sheets || [];
        if (configSheets.length > 0) {
            const enhancedSheets = configSheets.map(sheet => ({
                ...sheet,
                // Rebuild headers from schema
                headers: sheet.schema ? sheet.schema.map(col => col.columnName) : [],
                // Enhance existing schema with sample values for UI display
                schema: sheet.schema ? sheet.schema.map(col => ({
                    ...col,
                    sampleValues: col.sampleValues || ['Sample 1', 'Sample 2', 'Sample 3']
                })) : [],
                // Create minimal schema for storage if doesn't exist
                minimalSchema: sheet.schema ? createMinimalSchema(sheet.schema) : []
            }));
            
            setSheets(enhancedSheets);
            setFileName(config?.fileName || '');
            setFileType(config?.fileType || '');
            setSelectedSheetIndex(config?.selectedSheetIndex || 0);
            
            // Set selected sheet from config
            const selected = enhancedSheets.find(s => s.name === config?.selectedSheet) || enhancedSheets[0];
            if (selected) {
                setSelectedSheet(selected);
            }
        }
    }, [config?.sheets, config?.selectedSheet, config?.fileName, config?.fileType, config?.selectedSheetIndex]);

    // Table columns for schema display with editing capability
    const getSchemaColumns = (sheetIndex) => [
        {
            title: 'Column Name',
            dataIndex: 'columnName',
            key: 'columnName',
            width: 150,
            ellipsis: true,
            render: (text) => (
                <Text strong style={{ fontSize: '13px' }}>
                    {text}
                </Text>
            )
        },
        {
            title: 'Data Type',
            dataIndex: 'dataType',
            key: 'dataType',
            width: 110,
            render: (type, record) => (
                <Select
                    value={type}
                    size="small"
                    style={{ width: '100%' }}
                    onChange={(value) => updateSheetSchema(sheetIndex, record.key, { dataType: value })}
                >
                    <Option value="string">String</Option>
                    <Option value="integer">Integer</Option>
                    <Option value="float">Float</Option>
                    <Option value="boolean">Boolean</Option>
                    <Option value="date">Date</Option>
                </Select>
            )
        },
        {
            title: 'Sample Values',
            dataIndex: 'sampleValues',
            key: 'sampleValues',
            width: 200,
            render: (values) => (
                <div style={{ fontSize: '12px' }}>
                    {(values || []).slice(0, 2).map((val, idx) => (
                        <Tag key={idx} style={{ margin: '1px', fontSize: '11px' }}>
                            {String(val).length > 15 ? String(val).substring(0, 15) + '...' : val}
                        </Tag>
                    ))}
                </div>
            )
        },
        {
            title: 'Nullable',
            dataIndex: 'nullable',
            key: 'nullable',
            width: 100,
            align: 'center',
            render: (nullable) => (
                <Tag color={nullable ? 'orange' : 'green'} style={{ fontSize: '11px' }}>
                    {nullable ? 'Yes' : 'No'}
                </Tag>
            )
        }
    ];

    // Table columns for data preview - improved version
    const getPreviewColumns = (sheet) => {
        if (!sheet || !sheet.headers) return [];
        return sheet.headers.map((header, index) => ({
            title: (
                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    {header}
                </div>
            ),
            dataIndex: header,
            key: header,
            width: 150,
            ellipsis: {
                showTitle: true,
            },
            render: (text) => (
                <div style={{ fontSize: '12px' }}>
                    {String(text || '').length > 25 ?
                        <span title={String(text || '')}>
                            {String(text || '').substring(0, 25)}...
                        </span> :
                        text || ''
                    }
                </div>
            )
        }));
    };

    // Table columns for schema analysis
    const schemaColumns = [
        {
            title: 'Column',
            dataIndex: 'columnName',
            key: 'columnName',
            width: 150,
        },
        {
            title: 'Data Type',
            dataIndex: 'dataType',
            key: 'dataType',
            width: 120,
            render: (type) => <Tag color={getTypeColor(type)}>{type}</Tag>
        },
        {
            title: 'Sample Values',
            dataIndex: 'sampleValues',
            key: 'sampleValues',
            render: (values) => (
                <div>
                    {(values || []).map((val, idx) => (
                        <Tag key={idx} style={{ marginBottom: 2 }}>
                            {String(val).substring(0, 20)}
                        </Tag>
                    ))}
                </div>
            )
        },
        {
            title: 'Properties',
            key: 'properties',
            render: (_, record) => (
                <Space>
                    {record.nullable && <Tag color="orange">Nullable</Tag>}
                    {record.unique && <Tag color="green">Unique</Tag>}
                </Space>
            )
        }
    ];

    // Execute node (for testing)
    const executeNode = () => {
        if (!selectedSheet) {
            message.warning('Please select a sheet first');
            return;
        }

        // This would be the actual execution logic
        // Note: During actual execution, user will re-import Excel file
        // This is just for testing the optimized schema structure
        const executionResult = {
            nodeId: data?.id || 'excel_input_node',
            nodeType: 'excel_input',
            output: {
                fileName: fileName,
                sheetName: selectedSheet.name,
                headers: selectedSheet.headers || [],
                totalRows: selectedSheet.totalRows || 0,
                schema: selectedSheet.minimalSchema || selectedSheet.schema || [],
                // Note: Actual data will be loaded during workflow execution
                message: 'Optimized schema configured successfully. Data will be loaded during workflow execution.',
                storageOptimized: true
            }
        };

        console.log('Node execution result:', executionResult);
        message.success('Node configured successfully! Schema optimized for lightweight storage.');
    };

    return (
        <div>
            <Title level={4}>
                <FileExcelOutlined /> Excel/CSV Input Configuration
            </Title>

            {/* File Upload Section */}
            <Dragger {...handleFileUpload} style={{ marginBottom: 16 }}>
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to upload</p>
                <p className="ant-upload-hint">
                    Support for Excel (.xlsx, .xls) and CSV files
                </p>
            </Dragger>

            {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 8 }}>Analyzing file...</div>
                </div>
            )}

            {fileName && !loading && (
                <Alert
                    message={`File: ${fileName} (${fileType.toUpperCase()})`}
                    type="success"
                    style={{ marginBottom: 16 }}
                    action={
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={executeNode}
                            size="small"
                        >
                            Test Execute
                        </Button>
                    }
                />
            )}

            {/* File Analysis - Simplified Version */}
            {sheets.length > 0 && !loading && (
                <>
                    <Form.Item label={`Select Sheet (Total: ${sheets.length})`} style={{ marginBottom: 10, width: '100%' }}>
                        <Select
                            value={selectedSheetIndex}
                            onChange={(value) => {
                                setSelectedSheetIndex(value);
                                handleSheetIndexChange(value);
                            }}
                            style={{ width: '100%' }}
                        >
                            {sheets.map((sheet, index) => (
                                <Option key={index} value={index}>
                                    <TableOutlined /> {sheet.name} ({sheet.totalRows || 0} rows × {(sheet.headers || []).length} cols)
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* Schema Analysis for Selected Sheet */}
                    {selectedSheet && selectedSheet.schema && (
                        <Table
                            columns={getSchemaColumns(selectedSheetIndex)}
                            dataSource={selectedSheet.schema || []}
                            size="small"
                            pagination={false}
                            scroll={{ x: 700, y: 250 }}
                            bordered
                            style={{
                                backgroundColor: '#fafafa',
                            }}
                        />
                    )}

                    {/* Data Preview for Selected Sheet */}
                    {selectedSheet && selectedSheet.headers && (
                        <Table
                            title={() => (<div style={{ fontWeight: 'bold' }}>Showing sample preview (schema-based) of {selectedSheet.totalRows || 0} total rows</div>)}
                            columns={getPreviewColumns(selectedSheet)}
                            dataSource={generatePreviewData(selectedSheet)}
                            size="small"
                            pagination={false}
                            scroll={{ x: Math.max(600, (selectedSheet.headers || []).length * 150), y: 300 }}
                            bordered
                            style={{
                                backgroundColor: '#fafafa',
                                marginTop: 16
                            }}
                        />
                    )}
                </>
            )}

            {/* No File State */}
            {!fileName && !loading && (
                <Card>
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <WarningOutlined style={{ fontSize: '48px', color: '#faad14' }} />
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">No file uploaded yet</Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">Please upload an Excel or CSV file to configure this node</Text>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default ExcelInputNodeProperties;